import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useDataStore } from '../../store/dataStore'
import { OCAColors, Typography, Spacing } from '../../theme/colors'
import { IOSListSection } from '../ui/IOSListSection'
import { IOSListItem } from '../ui/IOSListItem'
import { MaterialIcon } from '../ui/MaterialIcon'
import type { Device as APIDevice, DeviceData, Location as APILocation } from '../../services/api'
import { buildLocationHierarchy } from '../../utils/locationHierarchy'
import { getDeviceStatus as getDeviceStatusUtil, isAlarmActive, isWarningState } from '../../utils/deviceStatus'
import type { Customer } from '../../types/locations'

// Enhanced types based on prototype
interface ThresholdValues {
  lowAlarm: number
  lowWarning: number
  highWarning: number
  highAlarm: number
}

interface Zone {
  id: string
  name: string
  type: string
  description: string

  // Location Details
  buildingNumber: string
  floor: string
  roomNumbers?: string[]

  // ICRA Information
  containmentLevel: 'Level 1' | 'Level 2' | 'Level 3' | 'Level 4'
  riskCategory: 'Low' | 'Intermediate' | 'High'
  workType: string

  // Environmental Requirements
  requiredPressureDifferential: number
  airChangesPerHour: number
  hepaFiltrationRequired: boolean

  // Current Status
  currentPressure: number
  pressureStatus: 'alarm' | 'warning' | 'normal' | 'offline'
  pressureTrend: 'up' | 'down' | 'stable'
  lastUpdate: Date

  // Devices
  devices: Device[]

  // Project Information
  projectStartDate: Date
  projectEndDate: Date
  projectManager: {
    name: string
    phone: string
  }
}

interface Device extends Omit<APIDevice, 'lastSeen'> {
  model: string
  serialNumber: string
  currentPressure: number
  connectionStatus: 'online' | 'offline' | 'connecting'
  batteryLevel?: number
  signalStrength?: number
  lastSeen: string
  thresholds: ThresholdValues
  currentData?: DeviceData
}

export const ZonesPage: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated, getAccessToken } = useAuth()

  // Global state from Zustand store
  const {
    devices: storeDevices,
    locations: storeLocations,
    deviceData: storeDeviceData,
    isLoadingDevices,
    isLoadingLocations,
    isLoadingDeviceData,
    devicesError,
    locationsError,
    deviceDataError,
    fetchAllData
  } = useDataStore()

  const [zones, setZones] = useState<Zone[]>([])
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'alarms' | 'warnings' | 'offline'>('all')

  // Hierarchical structure
  const [customers, setCustomers] = useState<Customer[]>([])
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set())
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set())
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set())

  // Function to determine zone status based on devices
  const getZoneStatus = (devices: Device[]): 'alarm' | 'warning' | 'normal' | 'offline' => {
    if (devices.length === 0) return 'offline'

    // Check for alarms first (highest priority) - use shared utility functions
    const hasAlarm = devices.some(d => {
      const data = deviceDataMap.get(d.deviceId)
      if (!data) return false
      return isAlarmActive(data.dp0_alarm_state) || isAlarmActive(data.dp1_alarm_state)
    })
    if (hasAlarm) return 'alarm'

    // Check for warnings (second priority)
    const hasWarning = devices.some(d => {
      const data = deviceDataMap.get(d.deviceId)
      if (!data) return false
      return isWarningState(data.dp0_alarm_state) || isWarningState(data.dp1_alarm_state)
    })
    if (hasWarning) return 'warning'

    // Check if all devices are offline
    const allOffline = devices.every(d => d.connectionStatus === 'offline')
    if (allOffline) return 'offline'

    return 'normal'
  }

  // Store device data map at component level so getZoneStatus can access it
  const [deviceDataMap, setDeviceDataMap] = useState<Map<string, DeviceData>>(new Map())

  // Count alarms in a zone
  const countAlarms = (zone: Zone): number => {
    return zone.devices.filter(device => {
      const data = deviceDataMap.get(device.deviceId)
      if (!data) return false
      return isAlarmActive(data.dp0_alarm_state) || isAlarmActive(data.dp1_alarm_state)
    }).length
  }

  // Count warnings in a zone
  const countWarnings = (zone: Zone): number => {
    return zone.devices.filter(device => {
      const data = deviceDataMap.get(device.deviceId)
      if (!data) return false
      return isWarningState(data.dp0_alarm_state) || isWarningState(data.dp1_alarm_state)
    }).length
  }

  // Count offline devices in a zone
  const countOffline = (zone: Zone): number => {
    return zone.devices.filter(device => device.connectionStatus === 'offline').length
  }

  const getPressureTrend = (): 'up' | 'down' | 'stable' => {
    // For now, return stable. In a real app, this would analyze historical data
    return 'stable'
  }

  // Generate reasonable defaults for enhanced zone properties
  const generateZoneDefaults = (location: APILocation) => {
    // Use location data to make educated guesses
    const locationName = (location.description || '').toLowerCase()

    let containmentLevel: 'Level 1' | 'Level 2' | 'Level 3' | 'Level 4' = 'Level 2' // Default
    let riskCategory: 'Low' | 'Intermediate' | 'High' = 'Intermediate' // Default
    let workType = 'Patient Care' // Default
    let requiredPressureDifferential = -0.01 // Default negative pressure
    let airChangesPerHour = 12 // Default
    let hepaFiltrationRequired = false // Default

    // Customize based on location type
    if (locationName.includes('or') || locationName.includes('surgery') || locationName.includes('operating')) {
      containmentLevel = 'Level 3'
      riskCategory = 'High'
      workType = 'Surgical Procedures'
      requiredPressureDifferential = -0.01
      airChangesPerHour = 20
      hepaFiltrationRequired = true
    } else if (locationName.includes('clean') || locationName.includes('pharm') || locationName.includes('sterile')) {
      containmentLevel = 'Level 4'
      riskCategory = 'High'
      workType = 'Sterile Compounding'
      requiredPressureDifferential = 0.005 // Positive pressure for cleanrooms
      airChangesPerHour = 30
      hepaFiltrationRequired = true
    } else if (locationName.includes('lab')) {
      containmentLevel = 'Level 2'
      riskCategory = 'Intermediate'
      workType = 'Laboratory Work'
      requiredPressureDifferential = -0.01
      airChangesPerHour = 15
    } else if (locationName.includes('icu') || locationName.includes('isolation')) {
      containmentLevel = 'Level 2'
      riskCategory = 'Intermediate'
      workType = 'Patient Care'
      requiredPressureDifferential = -0.01
      airChangesPerHour = 12
    }

    return {
      containmentLevel,
      riskCategory,
      workType,
      requiredPressureDifferential,
      airChangesPerHour,
      hepaFiltrationRequired
    }
  }

  // Build zones from API data
  const buildZonesFromData = (locations: APILocation[], devices: APIDevice[], dataMap: Map<string, DeviceData>) => {
    const devicesByLocation = new Map<string, Device[]>()

    // Store device data map in state for getZoneStatus to access
    setDeviceDataMap(dataMap)

    // Group enhanced devices by location
    devices.forEach(device => {
      if (!device.locationId) return

      const deviceData = deviceDataMap.get(device.deviceId)

      // Get device status using shared utility with correct priority
      const deviceStatus = getDeviceStatusUtil(
        deviceData?.dp0_alarm_state,
        deviceData?.dp1_alarm_state,
        deviceData?.timestamp
      )

      // Map status to connection status for backward compatibility
      const connectionStatus: 'online' | 'offline' | 'connecting' =
        deviceStatus === 'Offline' || deviceStatus === 'No Data' ? 'offline' : 'online'

      const enhancedDevice: Device = {
        ...device,
        model: device.deviceTypeId || 'G700',
        serialNumber: device.deviceId, // Use deviceId as serial for now
        currentPressure: deviceData?.dp0 || 0,
        connectionStatus,
        batteryLevel: undefined, // Not available in current API
        signalStrength: deviceData?.c_rssi,
        lastSeen: deviceData ? deviceData.timestamp : new Date().toISOString(),
        thresholds: {
          lowAlarm: deviceData?.dp0at ? -Math.abs(deviceData.dp0at) : -0.015,
          lowWarning: deviceData?.dp0wt ? -Math.abs(deviceData.dp0wt) : -0.012,
          highWarning: deviceData?.dp0wt ? Math.abs(deviceData.dp0wt) : 0.008,
          highAlarm: deviceData?.dp0at ? Math.abs(deviceData.dp0at) : 0.010
        },
        currentData: deviceData
      }

      if (!devicesByLocation.has(device.locationId)) {
        devicesByLocation.set(device.locationId, [])
      }
      devicesByLocation.get(device.locationId)!.push(enhancedDevice)
    })

    // Create zones from locations with devices
    const zonesData: Zone[] = []
    locations.forEach((location: APILocation) => {
      const locationDevices = devicesByLocation.get(location.locationId) || []
      if (locationDevices.length === 0) return

      const zoneDefaults = generateZoneDefaults(location)
      const avgPressure = locationDevices.length > 0
        ? locationDevices.reduce((sum, device) => sum + device.currentPressure, 0) / locationDevices.length
        : 0

      const zone: Zone = {
        id: location.locationId,
        name: location.name || location.locationId,
        type: location.address || 'Monitoring Zone',
        description: `Real-time pressure monitoring zone`,

        // Location details - derived from API data
        buildingNumber: location.address || 'Main Building',
        floor: 'Floor 1', // Default - could be extracted from location data
        roomNumbers: [location.name || location.locationId],

        // ICRA Information - generated based on location type
        ...zoneDefaults,

        // Current status from real data based on device alarm states
        currentPressure: avgPressure,
        pressureStatus: getZoneStatus(locationDevices),
        pressureTrend: getPressureTrend(),
        lastUpdate: new Date(),

        // Devices from API
        devices: locationDevices,

        // Project information - defaults (could be enhanced with real project data)
        projectStartDate: new Date(),
        projectEndDate: new Date(Date.now() + 6 * 30 * 24 * 60 * 60 * 1000), // 6 months from now
        projectManager: {
          name: 'Project Manager',
          phone: '(555) 000-0000'
        }
      }

      zonesData.push(zone)
    })

    return zonesData
  }

  // Refresh data from API
  const refreshData = async () => {
    if (!isAuthenticated) return

    const token = await getAccessToken()
    if (!token) {
      console.error('Unable to get access token')
      return
    }

    // Force refresh all data from API
    await fetchAllData(token, true)
  }

  // Fetch data from store on mount
  useEffect(() => {
    const loadData = async () => {
      if (!isAuthenticated) return

      const token = await getAccessToken()
      if (!token) {
        console.error('Unable to get access token')
        return
      }

      // Fetch all data from store (uses caching to prevent 429 errors)
      await fetchAllData(token)
    }

    loadData()
  }, [isAuthenticated])

  // Process store data into zones whenever it changes
  useEffect(() => {
    if (storeDevices.length === 0 || storeLocations.length === 0) return

    console.log('🏢 Zones: Processing store data...')
    console.log('🏢 Zones: Devices:', storeDevices.length, 'Locations:', storeLocations.length)

    // Convert Record to Map for buildZonesFromData
    const deviceDataMap = new Map<string, DeviceData>()
    Object.entries(storeDeviceData).forEach(([deviceId, dataArray]) => {
      if (dataArray.length > 0) {
        deviceDataMap.set(deviceId, dataArray[0]) // Use most recent data point
      }
    })

    console.log('🏢 Zones: Device data map size:', deviceDataMap.size)

    // Build zones from store data
    const zonesData = buildZonesFromData(storeLocations, storeDevices, deviceDataMap)
    console.log('🏢 Zones: Built', zonesData.length, 'zones')

    setZones(zonesData)

    // Build location hierarchy
    const hierarchy = buildLocationHierarchy(storeLocations)
    console.log('🏢 Zones: Built hierarchy with', hierarchy.customers.length, 'customers')
    setCustomers(hierarchy.customers)

    // Expand all by default for initial view
    const allCustomerIds = new Set(hierarchy.customers.map(c => c.locationId))
    setExpandedCustomers(allCustomerIds)
  }, [storeDevices, storeLocations, storeDeviceData])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'alarm': return OCAColors.danger
      case 'warning': return OCAColors.warning
      case 'normal': return OCAColors.success
      default: return OCAColors.gray500
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'alarm': return 'error'
      case 'warning': return 'warning'
      case 'normal': return 'check_circle'
      default: return 'help'
    }
  }

  // Generate status text with counts for alarms/warnings/offline
  // Show all relevant statuses, only show "Normal" if nothing else applies
  const getStatusText = (zone: Zone): string | undefined => {
    const statusParts: string[] = []

    // Check for alarms
    const alarmCount = countAlarms(zone)
    if (alarmCount > 0) {
      statusParts.push(`${alarmCount} Alarm${alarmCount !== 1 ? 's' : ''}`)
    }

    // Check for warnings
    const warningCount = countWarnings(zone)
    if (warningCount > 0) {
      statusParts.push(`${warningCount} Warning${warningCount !== 1 ? 's' : ''}`)
    }

    // Check for offline
    const offlineCount = countOffline(zone)
    if (offlineCount > 0) {
      statusParts.push(`${offlineCount} Offline`)
    }

    // If we have any status parts, join them with commas
    if (statusParts.length > 0) {
      return statusParts.join(', ')
    }

    // Only show "Normal" if all devices are online and normal
    return 'Normal'
  }

  const filteredZones = zones.filter(zone => {
    if (selectedFilter === 'alarms') return zone.pressureStatus === 'alarm'
    if (selectedFilter === 'warnings') return zone.pressureStatus === 'warning'
    if (selectedFilter === 'offline') return zone.pressureStatus === 'offline'
    return true
  })

  const handleZoneTap = (zone: Zone) => {
    console.log('Zone tapped:', zone.name, 'navigating to devices with filter')
    // Navigate to devices tab with location filter
    navigate('/devices', {
      state: {
        locationFilter: zone.id,
        locationName: zone.name
      }
    })
  }

  const toggleCustomer = (customerId: string) => {
    setExpandedCustomers(prev => {
      const newSet = new Set(prev)
      if (newSet.has(customerId)) {
        newSet.delete(customerId)
      } else {
        newSet.add(customerId)
      }
      return newSet
    })
  }

  const toggleSite = (siteId: string) => {
    setExpandedSites(prev => {
      const newSet = new Set(prev)
      if (newSet.has(siteId)) {
        newSet.delete(siteId)
      } else {
        newSet.add(siteId)
      }
      return newSet
    })
  }

  const toggleProject = (projectId: string) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev)
      if (newSet.has(projectId)) {
        newSet.delete(projectId)
      } else {
        newSet.add(projectId)
      }
      return newSet
    })
  }

  // Helper to get zone data for a location
  const getZoneForLocation = (locationId: string): Zone | undefined => {
    return zones.find(zone => zone.id === locationId)
  }

  // Recursive helper to check if a location or any descendant has a zone with the specified status
  const hasZoneWithStatus = (
    locationId: string,
    status: 'alarm' | 'warning' | 'offline',
    hierarchy: any // Customer, Site, Project, or Area
  ): boolean => {
    // Check if this location itself has a zone with the status
    const zone = getZoneForLocation(locationId)
    if (zone && zone.pressureStatus === status) return true

    // Check children based on hierarchy type
    if ('sites' in hierarchy && hierarchy.sites) {
      for (const site of hierarchy.sites) {
        if (hasZoneWithStatus(site.locationId, status, site)) return true
      }
    }
    if ('projects' in hierarchy && hierarchy.projects) {
      for (const project of hierarchy.projects) {
        if (hasZoneWithStatus(project.locationId, status, project)) return true
      }
    }
    if ('areas' in hierarchy && hierarchy.areas) {
      for (const area of hierarchy.areas) {
        const areaZone = getZoneForLocation(area.locationId)
        if (areaZone && areaZone.pressureStatus === status) return true
      }
    }

    return false
  }

  // Get the highest priority status for a location and all descendants
  // Priority: alarm (red) > warning (orange) > offline (grey) > normal (no border)
  const getHierarchyBorderColor = (
    locationId: string,
    hierarchy: any // Customer, Site, Project, or Area
  ): string | undefined => {
    // Check for alarms first (highest priority - red)
    if (hasZoneWithStatus(locationId, 'alarm', hierarchy)) {
      return OCAColors.danger
    }
    // Check for warnings (second priority - orange)
    if (hasZoneWithStatus(locationId, 'warning', hierarchy)) {
      return OCAColors.warning
    }
    // Check for offline (third priority - grey)
    if (hasZoneWithStatus(locationId, 'offline', hierarchy)) {
      return OCAColors.gray500
    }
    // No special status - no border
    return undefined
  }

  // Auto-expand hierarchy when filtering or to show devices with alarms/warnings
  useEffect(() => {
    if (customers.length === 0) return

    // Always expand all customers
    const allCustomerIds = new Set(customers.map(c => c.locationId))
    setExpandedCustomers(allCustomerIds)

    const sitesToExpand = new Set<string>()
    const projectsToExpand = new Set<string>()

    // For filtered views or to show alarms/warnings in "All" view
    if (selectedFilter === 'all') {
      // In "All" view, expand to show any alarms or warnings
      customers.forEach(customer => {
        // Check for alarms
        const hasAlarms = hasZoneWithStatus(customer.locationId, 'alarm', customer)
        const hasWarnings = hasZoneWithStatus(customer.locationId, 'warning', customer)

        if (hasAlarms || hasWarnings) {
          // Expand sites that contain alarms or warnings
          customer.sites?.forEach(site => {
            const siteHasAlarms = hasZoneWithStatus(site.locationId, 'alarm', site)
            const siteHasWarnings = hasZoneWithStatus(site.locationId, 'warning', site)

            if (siteHasAlarms || siteHasWarnings) {
              sitesToExpand.add(site.locationId)

              // Expand projects under sites that contain alarms or warnings
              site.projects?.forEach(project => {
                const projHasAlarms = hasZoneWithStatus(project.locationId, 'alarm', project)
                const projHasWarnings = hasZoneWithStatus(project.locationId, 'warning', project)

                if (projHasAlarms || projHasWarnings) {
                  projectsToExpand.add(project.locationId)
                }
              })
            }
          })

          // Expand projects under customers that contain alarms or warnings
          customer.projects?.forEach(project => {
            const projHasAlarms = hasZoneWithStatus(project.locationId, 'alarm', project)
            const projHasWarnings = hasZoneWithStatus(project.locationId, 'warning', project)

            if (projHasAlarms || projHasWarnings) {
              projectsToExpand.add(project.locationId)
            }
          })
        }
      })
    } else {
      // For filtered views (alarms, warnings, or offline)
      const status: 'alarm' | 'warning' | 'offline' =
        selectedFilter === 'alarms' ? 'alarm' :
        selectedFilter === 'warnings' ? 'warning' : 'offline'

      customers.forEach(customer => {
        if (hasZoneWithStatus(customer.locationId, status, customer)) {
          // Expand sites that contain matching zones
          customer.sites?.forEach(site => {
            if (hasZoneWithStatus(site.locationId, status, site)) {
              sitesToExpand.add(site.locationId)

              // Expand projects under sites that contain matching zones
              site.projects?.forEach(project => {
                if (hasZoneWithStatus(project.locationId, status, project)) {
                  projectsToExpand.add(project.locationId)
                }
              })
            }
          })

          // Expand projects under customers that contain matching zones
          customer.projects?.forEach(project => {
            if (hasZoneWithStatus(project.locationId, status, project)) {
              projectsToExpand.add(project.locationId)
            }
          })
        }
      })
    }

    setExpandedSites(sitesToExpand)
    setExpandedProjects(projectsToExpand)
  }, [selectedFilter, customers, zones])

  if (!isAuthenticated) {
    return (
      <div style={{
        textAlign: 'center',
        padding: Spacing.xl,
        color: OCAColors.textSecondary,
      }}>
        Please log in to view zones
      </div>
    )
  }

  // Show loading state while any data is loading
  const loading = isLoadingDevices || isLoadingLocations || isLoadingDeviceData
  const error = devicesError || locationsError || deviceDataError

  if (loading && zones.length === 0) {
    return (
      <div style={{
        backgroundColor: '#F2F2F7',
        minHeight: '100vh',
        paddingBottom: '140px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: Spacing.xl,
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: `3px solid ${OCAColors.separator}`,
          borderTop: `3px solid ${OCAColors.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: Spacing.md,
        }} />
        <p style={{
          color: OCAColors.secondaryLabel,
          margin: 0,
        }}>
          Loading zones data...
        </p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#F2F2F7',
        minHeight: '100vh',
        paddingBottom: '140px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: Spacing.xl,
      }}>
        <MaterialIcon name="error" size={48} color={OCAColors.danger} />
        <h2 style={{
          fontSize: Typography.fontSize.lg,
          fontWeight: Typography.fontWeight.semibold,
          color: OCAColors.label,
          margin: `${Spacing.md} 0 ${Spacing.sm} 0`,
        }}>
          Error Loading Zones
        </h2>
        <p style={{
          color: OCAColors.secondaryLabel,
          margin: `0 0 ${Spacing.lg} 0`,
          textAlign: 'center',
        }}>
          {error}
        </p>
        <button
          onClick={refreshData}
          style={{
            backgroundColor: OCAColors.primary,
            color: OCAColors.background,
            border: 'none',
            borderRadius: '8px',
            padding: `${Spacing.sm} ${Spacing.lg}`,
            fontSize: Typography.fontSize.base,
            fontWeight: Typography.fontWeight.medium,
            cursor: 'pointer',
          }}
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div style={{
      backgroundColor: '#F2F2F7',
      minHeight: '100vh',
      paddingBottom: '140px',
    }}>
      {/* Header */}
      <div style={{
        padding: Spacing.lg,
        backgroundColor: OCAColors.gray900,
        borderBottom: `0.5px solid ${OCAColors.separator}`,
        minHeight: '96px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: Spacing.sm,
          }}>
            <img
              src="/OmniIQ_AppIcon_1024x1024.png"
              alt="OMNI IQ"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
              }}
            />
            <h1 style={{
              fontSize: Typography.fontSize['2xl'],
              fontWeight: Typography.fontWeight.bold,
              color: OCAColors.background,
              margin: 0,
            }}>
              Zones
            </h1>
          </div>
          <button
            onClick={refreshData}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: Spacing.xs,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MaterialIcon name="refresh" size={24} color={OCAColors.background} />
          </button>
        </div>

        {/* Filter Segments */}
        <div style={{
          display: 'flex',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
          padding: '2px',
        }}>
          {[
            ['all', 'All'],
            ['alarms', 'Alarms'],
            ['warnings', 'Warnings'],
            ['offline', 'Offline']
          ].map(([filter, label]) => (
            <button
              key={filter}
              onClick={() => setSelectedFilter(filter as any)}
              style={{
                flex: 1,
                padding: `${Spacing.xs} ${Spacing.sm}`,
                fontSize: Typography.fontSize.sm,
                fontWeight: Typography.fontWeight.medium,
                backgroundColor: selectedFilter === filter ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                color: selectedFilter === filter ? OCAColors.background : 'rgba(255, 255, 255, 0.6)',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Hierarchical Zone List */}
      <IOSListSection title={`${filteredZones.length} ZONE${filteredZones.length !== 1 ? 'S' : ''}`}>
        {customers.map((customer) => {
          const customerZone = getZoneForLocation(customer.locationId)
          const isExpanded = expandedCustomers.has(customer.locationId)

          // Skip if no zone data
          if (!customerZone && !customer.sites?.length && !customer.projects?.length) return null

          // Apply status filter - check recursively through entire hierarchy
          if (selectedFilter !== 'all') {
            const status = selectedFilter === 'alarms' ? 'alarm' : selectedFilter === 'warnings' ? 'warning' : 'offline'
            if (!hasZoneWithStatus(customer.locationId, status, customer)) return null
          }

          return (
            <div key={customer.locationId}>
              {/* Customer Level */}
              <div
                onClick={() => toggleCustomer(customer.locationId)}
                style={{
                  backgroundColor: OCAColors.background,
                  padding: '12px 16px',
                  borderBottom: `0.5px solid ${OCAColors.separator}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: Spacing.sm,
                  cursor: 'pointer',
                }}
              >
                <MaterialIcon
                  name={isExpanded ? 'expand_more' : 'chevron_right'}
                  size={24}
                  color={OCAColors.secondaryLabel}
                />
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: Typography.fontSize.base,
                    fontWeight: Typography.fontWeight.semibold,
                    color: OCAColors.label,
                  }}>
                    {customer.name || customer.locationId}
                  </div>
                  <div style={{
                    fontSize: Typography.fontSize.sm,
                    color: OCAColors.secondaryLabel,
                  }}>
                    Customer
                  </div>
                </div>
              </div>

              {/* Expanded Sites and Projects */}
              {isExpanded && (
                <>
                  {/* Sites */}
                  {customer.sites?.map((site) => {
                    const siteZone = getZoneForLocation(site.locationId)
                    const isSiteExpanded = expandedSites.has(site.locationId)

                    // Apply status filter - check recursively through site hierarchy
                    if (selectedFilter !== 'all') {
                      const status = selectedFilter === 'alarms' ? 'alarm' : selectedFilter === 'warnings' ? 'warning' : 'offline'
                      if (!hasZoneWithStatus(site.locationId, status, site)) return null
                    }

                    return (
                      <div key={site.locationId}>
                        {/* Site with zone data - clickable */}
                        {siteZone ? (
                          <div style={{ paddingLeft: Spacing.xl }}>
                            <IOSListItem
                              icon={
                                <div style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '8px',
                                  backgroundColor: getStatusColor(siteZone.pressureStatus),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}>
                                  <MaterialIcon name={getStatusIcon(siteZone.pressureStatus)} size={20} color={OCAColors.background} />
                                </div>
                              }
                              title={site.name || site.locationId}
                              subtitle={`Site`}
                              value={`${siteZone.devices.length} device${siteZone.devices.length !== 1 ? 's' : ''}`}
                              onClick={() => handleZoneTap(siteZone)}
                              status={getStatusText(siteZone)}
                              statusColor={getStatusColor(siteZone.pressureStatus)}
                              borderColor={getHierarchyBorderColor(site.locationId, site)}
                            />
                          </div>
                        ) : (
                          /* Site header without zone data - expandable */
                          <div
                            onClick={() => toggleSite(site.locationId)}
                            style={{
                              backgroundColor: OCAColors.background,
                              padding: '12px 16px',
                              paddingLeft: `calc(${Spacing.xl} + 12px)`,
                              borderBottom: `0.5px solid ${OCAColors.separator}`,
                              display: 'flex',
                              alignItems: 'center',
                              gap: Spacing.sm,
                              cursor: 'pointer',
                            }}
                          >
                            <MaterialIcon
                              name={isSiteExpanded ? 'expand_more' : 'chevron_right'}
                              size={20}
                              color={OCAColors.secondaryLabel}
                            />
                            <div>
                              <div style={{ fontSize: Typography.fontSize.base, color: OCAColors.label }}>
                                {site.name || site.locationId}
                              </div>
                              <div style={{ fontSize: Typography.fontSize.sm, color: OCAColors.secondaryLabel }}>
                                Site
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Site's children (projects/areas) */}
                        {isSiteExpanded && (
                          <>
                            {site.projects?.map((project) => {
                              const projectZone = getZoneForLocation(project.locationId)
                              if (!projectZone) return null

                              // Apply status filter - check recursively through project hierarchy
                              if (selectedFilter !== 'all') {
                                const status = selectedFilter === 'alarms' ? 'alarm' : selectedFilter === 'warnings' ? 'warning' : 'offline'
                                if (!hasZoneWithStatus(project.locationId, status, project)) return null
                              }

                              return (
                                <IOSListItem
                                  key={project.locationId}
                                  icon={
                                    <div style={{
                                      width: '40px',
                                      height: '40px',
                                      borderRadius: '8px',
                                      backgroundColor: getStatusColor(projectZone.pressureStatus),
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}>
                                      <MaterialIcon name={getStatusIcon(projectZone.pressureStatus)} size={20} color={OCAColors.background} />
                                    </div>
                                  }
                                  title={project.name || project.locationId}
                                  subtitle={`Project`}
                                  value={`${projectZone.devices.length} device${projectZone.devices.length !== 1 ? 's' : ''}`}
                                  onClick={() => handleZoneTap(projectZone)}
                                  status={getStatusText(projectZone)}
                                  statusColor={getStatusColor(projectZone.pressureStatus)}
                                  paddingLeft={`calc(${Spacing.xl} * 2)`}
                                  borderColor={getHierarchyBorderColor(project.locationId, project)}
                                />
                              )
                            })}

                            {site.areas?.map((area) => {
                              const areaZone = getZoneForLocation(area.locationId)
                              if (!areaZone) return null

                              // Apply status filter - areas are leaf nodes
                              if (selectedFilter !== 'all') {
                                if (selectedFilter === 'alarms' && areaZone.pressureStatus !== 'alarm') return null
                                if (selectedFilter === 'warnings' && areaZone.pressureStatus !== 'warning') return null
                                if (selectedFilter === 'offline' && areaZone.pressureStatus !== 'offline') return null
                              }

                              return (
                                <IOSListItem
                                  key={area.locationId}
                                  icon={
                                    <div style={{
                                      width: '40px',
                                      height: '40px',
                                      borderRadius: '8px',
                                      backgroundColor: getStatusColor(areaZone.pressureStatus),
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}>
                                      <MaterialIcon name={getStatusIcon(areaZone.pressureStatus)} size={20} color={OCAColors.background} />
                                    </div>
                                  }
                                  title={area.name || area.locationId}
                                  subtitle={`Area`}
                                  value={`${areaZone.devices.length} device${areaZone.devices.length !== 1 ? 's' : ''}`}
                                  onClick={() => handleZoneTap(areaZone)}
                                  status={getStatusText(areaZone)}
                                  statusColor={getStatusColor(areaZone.pressureStatus)}
                                  paddingLeft={`calc(${Spacing.xl} * 2)`}
                                  borderColor={getHierarchyBorderColor(area.locationId, area)}
                                />
                              )
                            })}
                          </>
                        )}
                      </div>
                    )
                  })}

                  {/* Projects under customer */}
                  {customer.projects?.map((project) => {
                    const projectZone = getZoneForLocation(project.locationId)
                    const isProjectExpanded = expandedProjects.has(project.locationId)

                    // Apply status filter - check recursively through project hierarchy
                    if (selectedFilter !== 'all') {
                      const status = selectedFilter === 'alarms' ? 'alarm' : selectedFilter === 'warnings' ? 'warning' : 'offline'
                      if (!hasZoneWithStatus(project.locationId, status, project)) return null
                    }

                    return (
                      <div key={project.locationId}>
                        {/* Project with zone data */}
                        {projectZone ? (
                          <IOSListItem
                            icon={
                              <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '8px',
                                backgroundColor: getStatusColor(projectZone.pressureStatus),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}>
                                <MaterialIcon name={getStatusIcon(projectZone.pressureStatus)} size={20} color={OCAColors.background} />
                              </div>
                            }
                            title={project.name || project.locationId}
                            subtitle={`Project`}
                            value={`${projectZone.devices.length} device${projectZone.devices.length !== 1 ? 's' : ''}`}
                            onClick={() => handleZoneTap(projectZone)}
                            status={getStatusText(projectZone)}
                            statusColor={getStatusColor(projectZone.pressureStatus)}
                            paddingLeft={Spacing.xl}
                            borderColor={getHierarchyBorderColor(project.locationId, project)}
                          />
                        ) : (
                          /* Project header without zone data */
                          <div
                            onClick={() => toggleProject(project.locationId)}
                            style={{
                              backgroundColor: OCAColors.background,
                              padding: '12px 16px',
                              paddingLeft: `calc(${Spacing.xl} + 12px)`,
                              borderBottom: `0.5px solid ${OCAColors.separator}`,
                              display: 'flex',
                              alignItems: 'center',
                              gap: Spacing.sm,
                              cursor: 'pointer',
                            }}
                          >
                            <MaterialIcon
                              name={isProjectExpanded ? 'expand_more' : 'chevron_right'}
                              size={20}
                              color={OCAColors.secondaryLabel}
                            />
                            <div>
                              <div style={{ fontSize: Typography.fontSize.base, color: OCAColors.label }}>
                                {project.name || project.locationId}
                              </div>
                              <div style={{ fontSize: Typography.fontSize.sm, color: OCAColors.secondaryLabel }}>
                                Project
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Project's areas */}
                        {isProjectExpanded && project.areas?.map((area) => {
                          const areaZone = getZoneForLocation(area.locationId)
                          if (!areaZone) return null

                          // Apply status filter - areas are leaf nodes
                          if (selectedFilter !== 'all') {
                            if (selectedFilter === 'alarms' && areaZone.pressureStatus !== 'alarm') return null
                            if (selectedFilter === 'warnings' && areaZone.pressureStatus !== 'warning') return null
                            if (selectedFilter === 'offline' && areaZone.pressureStatus !== 'offline') return null
                          }

                          return (
                            <IOSListItem
                              key={area.locationId}
                              icon={
                                <div style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '8px',
                                  backgroundColor: getStatusColor(areaZone.pressureStatus),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}>
                                  <MaterialIcon name={getStatusIcon(areaZone.pressureStatus)} size={20} color={OCAColors.background} />
                                </div>
                              }
                              title={area.name || area.locationId}
                              subtitle={`Area`}
                              value={`${areaZone.devices.length} device${areaZone.devices.length !== 1 ? 's' : ''}`}
                              onClick={() => handleZoneTap(areaZone)}
                              status={getStatusText(areaZone)}
                              statusColor={getStatusColor(areaZone.pressureStatus)}
                              paddingLeft={`calc(${Spacing.xl} * 2)`}
                              borderColor={getHierarchyBorderColor(area.locationId, area)}
                            />
                          )
                        })}
                      </div>
                    )
                  })}
                </>
              )}
            </div>
          )
        })}
      </IOSListSection>

      {filteredZones.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: Spacing.xl,
          color: OCAColors.secondaryLabel,
          fontSize: Typography.fontSize.base,
        }}>
          No zones found for selected filter
        </div>
      )}
    </div>
  )
}